import { Construction, Server, Package, KeyRound, ScrollText, Building } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { Permission } from '@/lib/api/types';

/*
  Administration.

  Deliberately a map rather than a working settings screen. Phase 1 (docs/10-roadmap.md) puts
  tenant settings, relay management, agent release publishing and API keys behind later
  phases, and a settings form that silently does nothing is worse than an honest statement of
  what is not built. Each panel names the permission that will gate it and the phase that
  delivers it, and the panels the current account could not use are marked as such.
*/

interface AdminArea {
  readonly title: string;
  readonly Icon: typeof Server;
  readonly permission: Permission;
  readonly phase: string;
  readonly description: string;
}

const AREAS: readonly AdminArea[] = [
  {
    title: 'Tenant settings',
    Icon: Building,
    permission: 'platform.manage',
    phase: 'Phase 1 backend, console surface pending',
    description:
      'Tenant name, default MFA enforcement, session retention, and the login alert thresholds that trigger an email on a new IP or country.',
  },
  {
    title: 'Policy',
    Icon: ScrollText,
    permission: 'policy.manage',
    phase: 'Phase 4',
    description:
      'Per organization and per group policy: consent requirement, recording policy, file transfer, elevation, and whether relay-only transport is forced. Policy is signature verified on the endpoint, so a change here is enforced locally rather than trusted from the server.',
  },
  {
    title: 'Relays',
    Icon: Server,
    permission: 'platform.manage',
    phase: 'Phase 2',
    description:
      'TURN relay fleet, regional pinning for data residency, and measured relay health. Relays forward packets they cannot decrypt, so adding capacity is not a trust decision.',
  },
  {
    title: 'Agent releases',
    Icon: Package,
    permission: 'release.manage',
    phase: 'Phase 5',
    description:
      'Publish signed agent builds to the stable, beta and internal channels. Manifests are signed and verified by the updater before anything is applied.',
  },
  {
    title: 'API keys',
    Icon: KeyRound,
    permission: 'apikey.manage',
    phase: 'Phase 5',
    description:
      'Service credentials for RMM and PSA integrations. Session creation and every dangerous permission are not grantable to a key by default, because starting a remote session is a human act with a named accountable technician.',
  },
];

export function AdministrationPage(): React.JSX.Element {
  const { can } = useAuth();

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Administration"
        description="Tenant-wide configuration. Each area names the permission that gates it and the phase that delivers it."
      />

      <Alert tone="info" title="Phase 1 scope">
        Identity, tenancy and device enrollment are what Phase 1 delivers. The panels below are
        stubs on purpose: a settings form that appears to save and does not is worse than a
        clear statement of what is not built yet.
      </Alert>

      <ul className="grid gap-4 lg:grid-cols-2">
        {AREAS.map((area) => {
          const permitted = can(area.permission);
          const Icon = area.Icon;
          return (
            <li key={area.title}>
              <Card className="h-full">
                <CardHeader
                  title={
                    <span className="flex items-center gap-2">
                      <Icon className="size-4 text-fg-subtle" aria-hidden="true" />
                      {area.title}
                    </span>
                  }
                  action={
                    <Badge tone={permitted ? 'accent' : 'neutral'}>
                      {permitted ? 'You hold this' : 'Not permitted'}
                    </Badge>
                  }
                />
                <CardBody>
                  <p className="text-sm text-fg-muted">{area.description}</p>
                  <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs">
                    <div>
                      <dt className="inline text-fg-subtle">Permission: </dt>
                      <dd className="inline font-mono text-fg-muted">{area.permission}</dd>
                    </div>
                    <div>
                      <dt className="inline text-fg-subtle">Delivered: </dt>
                      <dd className="inline text-fg-muted">{area.phase}</dd>
                    </div>
                  </dl>
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-fg-subtle">
                    <Construction className="size-3.5" aria-hidden="true" />
                    Not implemented in this build.
                  </p>
                </CardBody>
              </Card>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
